import { useState, useEffect, useContext } from 'react';
import * as Sentry from '@sentry/browser';
import { dtos } from '@shd/jslib/models';
import axios from '@helpers/axios';
import { getFacebookAuthResponse, useFacebookScopes } from '@helpers/facebook';
import { TeamContext } from '@services/Session';
import { errorUtil, strogging } from '@shd/jslib/infra';
import { DateTime } from 'luxon';
import ApiError from '@helpers/ApiError';
import { analytics } from '@services/analytics';

export interface FacebookChannel {
  id: string;
  name: string;
  picture?: {
    data: {
      height: number;
      is_silhouette: boolean;
      url: string;
      width: number;
    };
  };
  label: string;
  type: string;
  tasks?: string[];
  disabled?: boolean;
  email?: string;
  link?: string;
  error?: string;
}

const expirationThreshold = 60 * 60 * 24 * 20; // 20 days

interface FacebookConnectionOptions {
  liveTeamId?: string | null;
  onComplete?: () => void;
}

export const useFacebookConnection = ({
  liveTeamId = null,
  onComplete = () => {},
}: FacebookConnectionOptions) => {
  const teamStore = useContext(TeamContext);
  const teamId = liveTeamId || teamStore.id;

  const fbScopes = useFacebookScopes();

  const [publishFB, setPublishFB] = useState<dtos.PublishFb | null>(null);
  const [prevLinkedChannel, setPrevLinkedChannel] =
    useState<dtos.PublishFb | null>(null);

  const [loading, setLoading] = useState(true);
  const [linkingLoading, setLinkingLoading] = useState(false);
  const [linkingError, setLinkingError] = useState<ApiError | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [showIntroModal, setShowIntroModal] = useState(false);
  const [showHowToSetUpModal, setShowHowToSetUpModal] = useState(false);
  const [showLinkingModal, setShowLinkingModal] = useState(false);
  const [showLinkingSuccess, setShowLinkingSuccess] = useState(false);
  const [isLivestreamEligible, setIsLivestreamEligible] = useState(false);
  const [stepDestinationTitle, setStepDestinationTitle] = useState(
    'Where do you want to stream to?'
  );
  const [destinationGoBack, setDestinationGoBack] = useState<() => void>();
  const [showExpiration, setShowExpiration] = useState<{
    daysUntilExpiration?: number;
  }>({});

  // Fetch linked FB channel on load
  useEffect(() => {
    axios
      .get<dtos.PublishFb>('/api/facebook_auth', { params: { teamId } })
      .then((res) => {
        const fbPublish = res.data || null;
        setPublishFB(fbPublish);
        setPrevLinkedChannel(fbPublish);

        // Check expiration
        if (fbPublish?.accessTokenExpirationTs) {
          const ts = Date.now();
          if (
            ts / 1000 >
            fbPublish.accessTokenExpirationTs - expirationThreshold
          ) {
            const now = DateTime.fromMillis(ts);
            const expiration = DateTime.fromSeconds(
              fbPublish.accessTokenExpirationTs
            );
            const daysUntilExpiration = Math.round(
              expiration.diff(now, ['days']).toObject().days ?? 0
            );
            setShowExpiration({ daysUntilExpiration });
          }
        }

        // If missing expiration, fetch via token debug API
        if (fbPublish?.accessToken && !fbPublish.accessTokenExpirationTs) {
          const tokenDebugUrl = `https://graph.facebook.com/debug_token?input_token=${fbPublish.accessToken}&access_token=${import.meta.env.VITE_APP_FACEBOOK_APP_ID}|${import.meta.env.VITE_APP_FACEBOOK_APP_SECRET}`;

          axios
            .get(tokenDebugUrl)
            .then((response) => {
              const { data } = response.data;
              axios
                .put('/api/facebook_auth', {
                  id: fbPublish._id,
                  accessTokenExpirationTs: data.data_access_expires_at,
                })
                .catch((e) => {
                  Sentry.captureException(e);
                  setError('Failed to update token expiration');
                });
            })
            .catch((e) => {
              strogging.error(
                'Failed to call FB debug_token',
                errorUtil.errorPayload(e)
              );
            });
        }

        setLoading(false);
      })
      .catch((e) => {
        Sentry.captureException(e);
        setError('Failed to fetch Facebook connection');
        setLoading(false);
      });
  }, [teamId]);

  const onLinkFacebookClick = () => {
    analytics.track('Link Streaming Destination Clicked', {
      destinationType: 'Facebook',
      teamId,
    });
    setPrevLinkedChannel(publishFB);
    setShowIntroModal(true);
  };

  const onUnlinkFacebookClick = async () => {
    try {
      await axios.delete('/api/facebook_auth', { data: { teamId } });
      setPublishFB(null);
      setShowExpiration({});
    } catch (e) {
      Sentry.captureException(e);
      setError('Failed to unlink Facebook');
    }
  };

  const onClose = () => {
    setPublishFB(prevLinkedChannel);
    setShowIntroModal(false);
    setShowLinkingModal(false);
    setShowHowToSetUpModal(false);
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const responseFacebook = async (res: any) => {
    if (res.status && res.status === 'unknown') {
      Sentry.captureMessage('Facebook API responded with an unknown status', {
        level: 'warning',
        extra: res,
      });
      strogging.log('facebook response error', res);
      return;
    }
    setShowLinkingModal(true);
  };

  const onSave = async (selectedChannel: FacebookChannel) => {
    setShowLinkingModal(false);
    setShowIntroModal(false);

    try {
      const authResponse = await getFacebookAuthResponse();

      if (!authResponse?.accessToken) {
        throw new ApiError('No Facebook access token found');
      }

      setLinkingLoading(true);

      const payload = {
        authResponse,
        linkedChannel: selectedChannel,
        teamId,
      };

      axios
        .post('/api/facebook_auth', payload)
        .then((response) => {
          analytics.track('Streaming Destination Linked', {
            destinationType: 'Facebook',
            destinationSubtype: selectedChannel.type,
            teamId,
          });

          const { accessTokenExpirationTs } = response.data;
          const ts = Date.now();

          if (ts / 1000 > accessTokenExpirationTs - expirationThreshold) {
            const now = DateTime.fromMillis(ts);
            const expiration = DateTime.fromSeconds(accessTokenExpirationTs);
            const daysUntilExpiration = Math.round(
              expiration.diff(now, ['days']).toObject().days ?? 0
            );
            setShowExpiration({ daysUntilExpiration });
          }

          setPublishFB(response.data.publishFb);
          onComplete();
          setLinkingError(null);
          setLinkingLoading(false);
          setShowLinkingSuccess(true);
          setIsLivestreamEligible(response.data.isLivestreamEligible);
        })
        .catch((e) => {
          const apiError = new ApiError(e);
          Sentry.captureException(e);
          strogging.error('Failed to link Facebook', {
            message: apiError.message,
            details: apiError.details,
          });
          setLinkingError(apiError);
          setLinkingLoading(false);
          setShowLinkingSuccess(false);
        });
    } catch (e) {
      const msg = errorUtil.errorMessage(e);
      Sentry.captureException(e);
      setLinkingError(new ApiError(msg || 'Unknown error'));
    }
  };

  return {
    loading,
    publishFB,
    fbScopes,
    showIntroModal,
    showHowToSetUpModal,
    showLinkingModal,
    stepDestinationTitle,
    showLinkingSuccess,
    isLivestreamEligible,
    destinationGoBack,
    setShowIntroModal,
    setShowHowToSetUpModal,
    setShowLinkingModal,
    setStepDestinationTitle,
    setDestinationGoBack,
    onLinkFacebookClick,
    onUnlinkFacebookClick,
    onClose,
    responseFacebook,
    onSave,
    linkingLoading,
    linkingError,
    showExpiration,
    error,
    setError,
    setPublishFB,
  };
};
